// =====================================================
// WebTemplate Vue 進入點 (Multi-page / Razor embedded Vue components)
// 掃描頁面所有 [data-vue-component] 並逐一 mount。
//
// 新增元件流程:
//   1. src/ 下寫 .vue 元件
//   2. componentRegistry 加一行 'kebab-name': () => import('./path/Xxx.vue')
//   3. Razor View 放 <div data-vue-component="kebab-name"></div>
//      (要帶 props 加 data-vue-props='{"foo":1}')
// =====================================================

// ── PathBase 自救 fallback ─────────────────────────────
// 正常情況 Razor _VueScriptsPartial 已注入 __APP_BASE__ / __VITE_BASE__,
// 但若有 Layout 漏掛 partial,從 import.meta.url 反推 base,避免 ajax 打到 host root。
; (function ensurePathBase() {
  try {
    if (typeof window === 'undefined') return
    if (window.__APP_BASE__ && globalThis.__VITE_BASE__) return
    const u = new URL(import.meta.url)
    const m = u.pathname.match(/^(.*?)\/dist\//i)
    const base = m ? (m[1] + '/') : '/'
    if (!window.__APP_BASE__) window.__APP_BASE__ = base
    if (!globalThis.__VITE_BASE__) globalThis.__VITE_BASE__ = base + 'dist/'
    console.info('[App] PathBase fallback applied:', window.__APP_BASE__)
  } catch (e) { /* ignore */ }
})()

// ── 修復 Bootstrap modal 關閉後 body 殘留 scroll-lock ──────────────
// 症狀:關 modal 後 <body> 的 overflow:hidden / .modal-open 偶爾沒被清掉 → 整頁無法捲動。
; (function fixModalScrollLock() {
  try {
    if (typeof document === 'undefined') return
    document.addEventListener('hidden.bs.modal', () => {
      setTimeout(() => {
        if (!document.querySelector('.modal.show')) {
          document.body.classList.remove('modal-open')
          document.body.style.removeProperty('overflow')
          document.body.style.removeProperty('padding-right')
          document.querySelectorAll('.modal-backdrop').forEach(b => b.remove())
        }
      }, 0)
    })
  } catch (e) { /* ignore */ }
})()

import { createApp, defineAsyncComponent } from 'vue'

// Bootstrap 5 的 JS（modal / dropdown / collapse）。
// ⚠⚠ **這裡刻意不 import bootstrap 的 CSS**。
//    Bootstrap CSS 由下面的 base.css 用 `@import ... layer(bootstrap)` 載入，
//    分層才會生效。2026-08-25 之前這裡多了一行
//    `import 'bootstrap/dist/css/bootstrap.min.css'`，等於**把 Bootstrap 載了兩次**：
//    一份沒有 layer（這一行）、一份有 layer（base.css）。
//    沒有 layer 的規則優先權比任何具名 layer 都高，於是那份直接打敗整個分層設計。
//    症狀是 dev 跟 build 畫面不一致（dev 下 Bootstrap 的 body 樣式蓋掉 wwwroot 範本 CSS）。
//    **不要把那一行加回來。**
import 'bootstrap'

// 全域 CSS (Bootstrap CSS + assets/main.css,經 @layer 分層 → 見 base.css 說明)
import './assets/base.css' //import './assets/main.css'

// Global utilities / API
import Common from './Common/Common.js'
import { myAjax } from './Common/api.js'
// 離線處理器（2026-10-01）：離線時查工項主檔／編碼規則等參考資料改用本機下載好的資料，見該檔檔頭
import './Common/offlineReference.js'
import Swal from 'sweetalert2'

// ── 自動 retry vite chunk preload 失敗 ──
// 冷啟動偶發第一個 chunk 載失敗 → import() throw → 畫面空白;自動 reload 一次,
// sessionStorage 防 infinite loop。
window.addEventListener('vite:preloadError', (event) => {
  const flagKey = '__vite_preload_retry__'
  if (sessionStorage.getItem(flagKey)) {
    sessionStorage.removeItem(flagKey)
    console.error('[App] vite preload error after retry,不再 reload', event.payload)
    return
  }
  sessionStorage.setItem(flagKey, '1')
  console.warn('[App] vite preload 失敗,自動 reload 一次', event.payload)
  window.location.reload()
})
window.addEventListener('load', () => {
  setTimeout(() => sessionStorage.removeItem('__vite_preload_retry__'), 3000)
})

// Expose for Razor inline scripts
window.common = Common
window.myAjax = myAjax
window.Swal = Swal
window.swal = Swal

// ── 元件註冊表 ──
const componentRegistry = {
  'home-news': () => import('./Backend/Home/HomeNews.vue'),
  'login': () => import('./Backend/Login/Login.vue'),
    // 2026-09-15 登入頁三畫面任務：忘記密碼／管理者啟用兩頁的掛載點＋登入頁
    // 送出前的個資同意跳窗（跨頁共用元件，全域註冊才能在 Login.vue 直接用
    // <privacy-consent-modal>，不必個別 import）。
    'forgot-password': () => import('./Backend/Login/ForgotPassword.vue'),
    'admin-activate': () => import('./Backend/Login/AdminActivate.vue'),
    'privacy-consent-modal': () => import('./Common/PrivacyConsentModal.vue'),
    'sample-list': () => import('./Backend/Sample/SampleList.vue'),
    'comm-pagination': () => import('./Common/Pagination.vue'),
    'comm-modal': () => import('./Common/Modal.vue'),
    'exception-report-widget': () => import('./Common/ExceptionReportWidget.vue'),
    'network-status-widget': () => import('./Common/NetworkStatusWidget.vue'),
    'role': () => import('./Backend/Role/RoleList.vue'),
    'menu': () => import('./Backend/Menu/MenuList.vue'),
    'user-list': () => import('./Backend/UserManager/UsersList.vue'),
    'project-item-code': () => import('./Backend/ItemCode/ProjectItemCode.vue'),
    'item-code-rule': () => import('./Backend/ItemCode/ItemCodeRule.vue'),
    'org-budget-import': () => import('./Backend/Template/OrgBudgetImport.vue'),
    // ── PCECUI demo 移植（純 UI 階段，資料為元件內示範資料）──
    'project-carbon-factor': () => import('./Backend/ItemCode/ProjectCarbonFactor.vue'),
    'material-carbon-factor': () => import('./Backend/ItemCode/MaterialCarbonFactor.vue'),
    'green-item': () => import('./Backend/ItemCode/GreenItem.vue'),
    'bulk-material': () => import('./Backend/ItemCode/BulkMaterial.vue'),
    'login-records': () => import('./Backend/System/LoginRecords.vue'),
    'security-log': () => import('./Backend/System/SecurityLog.vue'),
    'system-announcements': () => import('./Backend/System/SystemAnnouncements.vue'),
    'exception-report': () => import('./Backend/System/ExceptionReport.vue'),
    'sys-parameter-list': () => import('./Backend/SysParameter/SysParameterList.vue'),
    'plan-maintenance': () => import('./Backend/Template/PlanMaintenance.vue'),
    // ⚠ 跟上面那支是兩個不同的功能：維護＝管理範本本身，產製＝用範本產生計畫書
    'plan-production': () => import('./Backend/Template/PlanProduction.vue'),
    'list-flowchart': () => import('./Backend/Template/ListFlowchart.vue'),
    'inspection-standard': () => import('./Backend/Template/InspectionStandard.vue'),
    'inspection-record': () => import('./Backend/Template/InspectionRecord.vue'),
    'account-change-log': () => import('./Backend/UserManager/AccountChangeLog.vue'),
    'carbon-estimation-list': () => import('./Backend/Carbon/CarbonEstimationList.vue'),
    'carbon-basic': () => import('./Backend/Carbon/CarbonBasic.vue'),
    'carbon-edit': () => import('./Backend/Carbon/CarbonEdit.vue'),
    // ── 契約編製模組（PCECUI demo 移植，純 UI 階段，不接後端）──
    // ⚠ 走標準模式登記在這裡，不是 subComponent.js —— 那是 Budget 模組的既有安排
    //   （CLAUDE.md 有說明原因），契約模組沒有理由跟著繞道。
    'contract-list': () => import('./Backend/Contract/ContractList.vue'),
    'contract-add': () => import('./Backend/Contract/ContractAdd.vue'),
    // 分標／併標共用同一支元件，用 data-vue-props 的 mode 區分
    'contract-add-project': () => import('./Backend/Contract/ContractAddProject.vue'),
    'contract-project-select': () => import('./Backend/Contract/ContractProjectSelect.vue'),
    'contract-sub-item-choose': () => import('./Backend/Contract/ContractSubItemChoose.vue'),
    'contract-basic': () => import('./Backend/Contract/ContractBasic.vue'),
    'contract-edit': () => import('./Backend/Contract/ContractEdit.vue'),
}

import { setCompoment } from './subComponent.js'; //外部單位註冊用
setCompoment(componentRegistry);

// 全域註冊所有 registry 元件:任何 .vue 內可直接用 kebab 標籤引用子元件
// (如 <comm-pagination>),不必逐一 import。local import 同名時 local 優先。
function registerGlobalComponents(app) {
  for (const [cname, loader] of Object.entries(componentRegistry)) {
    app.component(cname, defineAsyncComponent(loader))
  }
}

// UiContainer singleton — 全域 confirm/alert/toast/loading (@/Common/ui.js)
let uiContainerMounted = false
async function mountUiContainerOnce() {
  if (uiContainerMounted) return
  uiContainerMounted = true
  const mod = await import('./Common/UiContainer.vue')
  const Container = mod.default || mod
  const host = document.createElement('div')
  host.id = 'app-ui-container'
  document.body.appendChild(host)
  createApp(Container).mount(host)
}

// ── 防護:偶發 chunk 被伺服器回 204(空內容) → createApp(undefined) 整頁空白 ──
function isValidComponent(c) {
  return !!c && (typeof c === 'object' || typeof c === 'function')
}
function tryRecoverOnce(name, reason) {
  const key = '__app_reload_' + name
  if (sessionStorage.getItem(key)) {
    sessionStorage.removeItem(key)
    console.error(`[App] "${name}" 重新整理後仍載入失敗 (${reason})。請檢查 IIS / 反向代理 / 壓縮設定。`)
    return false
  }
  sessionStorage.setItem(key, '1')
  console.warn(`[App] "${name}" 載入異常 (${reason}),自動重新整理一次嘗試恢復…`)
  location.reload()
  return true
}

async function bootstrap() {
  await mountUiContainerOnce()

  const targets = document.querySelectorAll('[data-vue-component]')
  for (const el of targets) {
    const name = el.getAttribute('data-vue-component')
    const loader = componentRegistry[name]
    if (!loader) {
      console.warn(`[App] Unknown Vue component: "${name}"`)
      continue
    }

    let props = {}
    const raw = el.getAttribute('data-vue-props')
    if (raw) {
      try {
        props = JSON.parse(raw)
      } catch (e) {
        console.error(`[App] data-vue-props parse error for "${name}":`, raw)
      }
    }

    try {
      const mod = await loader()
      const Component = mod && (mod.default || mod)
      if (!isValidComponent(Component)) {
        if (tryRecoverOnce(name, 'empty-module / 204')) return
        continue
      }
      const app = createApp(Component, props)
      registerGlobalComponents(app)
      app.provide('common', Common)
      app.provide('myAjax', myAjax)
      app.config.globalProperties.$common = Common
      app.config.globalProperties.$myAjax = myAjax
      // 全域 errorHandler — 把 mount/runtime 階段錯誤連同元件上下文一起印出
      app.config.errorHandler = (err, instance, info) => {
        try {
          const compName = (instance && (instance.$options?.name || instance.$?.type?.__name)) || '(unknown)'
          console.error(`[App Vue errorHandler] "${name}" 內元件 [${compName}] info=[${info}]`, err)
        } catch (e2) {
          console.error('[App Vue errorHandler] 解析錯誤上下文失敗', e2, '原始錯誤:', err)
        }
      }
      app.mount(el)
      sessionStorage.removeItem('__app_reload_' + name)
    } catch (err) {
      console.error(`[App] Failed to mount "${name}"`, err)
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap)
} else {
  bootstrap()
}

// Razor inline script 手動掛載用
window.mountVueComponent = async function (name, target, props = {}) {
  const loader = componentRegistry[name]
  if (!loader) throw new Error(`Unregistered Vue component: ${name}`)
  const el = typeof target === 'string' ? document.querySelector(target) : target
  if (!el) throw new Error('Target element not found')
  const mod = await loader()
  const Component = mod.default || mod
  const app = createApp(Component, props)
  registerGlobalComponents(app)
  app.provide('common', Common)
  app.provide('myAjax', myAjax)
  app.config.globalProperties.$common = Common
  app.config.globalProperties.$myAjax = myAjax
  app.mount(el)
  return app
}
